// /app/frontend/src/hooks/useLoungeWebRTC.js
import { useEffect, useRef, useState, useCallback } from 'react';
import { debugLog } from '../utils/debugLog';
import { safeDetachAblyChannel } from '../lib/realtime';

/**
 * useLoungeWebRTC
 *
 * Props:
 *  - ably: Ably Realtime client instance
 *  - userId: string (current user's ID)
 *  - displayName: string (for presence data)
 *
 * This hook:
 *  - Joins Ably presence in a lounge channel
 *  - Maintains a participants list from presence
 *  - Pairs users into 1:1 matches: [0,1], [2,3], ...
 *  - For your pair, uses a pair-specific signaling channel
 *  - Creates a single RTCPeerConnection to your partner
 *  - Exposes localStream, remoteStream, participants, status, and controls
 */

// Ticket 22: Room-specific channel (replaced by dynamic channel per room)
// const LOUNGE_CHANNEL = 'lounge:lifestyle'; // OLD - now derived from roomKey

const ICE_SERVERS = [
  {
    urls: ['stun:us-turn3.xirsys.com'],
  },
  {
    username:
      'BHDkg0XeIBfjwy2Ey_uj7LXf_EaB_Ey-7-JVzBpX9EEhKklT8Qk8cpqwBHWp2byDAAAAAGkioBlVbml0eUxvdmU=',
    credential: 'fddb7dfa-c82f-11f0-ae14-0242ac140004',
    urls: [
      'turn:us-turn3.xirsys.com:80?transport=udp',
      'turn:us-turn3.xirsys.com:3478?transport=udp',
      'turn:us-turn3.xirsys.com:80?transport=tcp',
      'turn:us-turn3.xirsys.com:3478?transport=tcp',
      'turns:us-turn3.xirsys.com:443?transport=tcp',
      'turns:us-turn3.xirsys.com:5349?transport=tcp',
    ],
  },
];

export function normalizeLoungeSex(value) {
  const normalized = String(value || '').trim().toLowerCase();

  if (['male', 'man', 'm'].includes(normalized)) return 'male';
  if (['female', 'woman', 'f'].includes(normalized)) return 'female';

  return null;
}

export function buildLoungePresenceData({
  displayName,
  sex,
  joinedAt = Date.now(),
}) {
  return {
    displayName,
    sex: normalizeLoungeSex(sex),
    joinedAt,
  };
}

function shuffleArray(items, randomFn = Math.random) {
  const copy = [...items];

  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(randomFn() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
}

export function selectEligibleLoungePartner({
  mappedParticipants,
  userId,
  currentUserSex,
  blockedUsers = [],
  lastSkippedId = null,
  currentPartnerId = null,
  randomFn = Math.random,
}) {
  const normalizedCurrentUserSex = normalizeLoungeSex(currentUserSex);
  if (!normalizedCurrentUserSex) return null;

  const blockedSet = new Set(blockedUsers);

  const eligibleCandidates = mappedParticipants.filter((participant) => {
    if (!participant || participant.clientId === userId) return false;
    if (blockedSet.has(participant.clientId)) return false;

    const candidateSex = normalizeLoungeSex(participant.sex);
    if (!candidateSex) return false;

    if (normalizedCurrentUserSex === 'male') {
      return candidateSex === 'female';
    }

    if (normalizedCurrentUserSex === 'female') {
      return candidateSex === 'male' || candidateSex === 'female';
    }

    return false;
  });

  if (!eligibleCandidates.length) return null;

  if (
    currentPartnerId &&
    eligibleCandidates.some((candidate) => candidate.clientId === currentPartnerId)
  ) {
    return currentPartnerId;
  }

  const candidatesWithoutLastSkipped = lastSkippedId
    ? eligibleCandidates.filter((candidate) => candidate.clientId !== lastSkippedId)
    : eligibleCandidates;

  const finalCandidates = candidatesWithoutLastSkipped.length
    ? candidatesWithoutLastSkipped
    : eligibleCandidates;

  const [selected] = shuffleArray(finalCandidates, randomFn);
  return selected?.clientId || null;
}

export function useLoungeWebRTC({ ably, userId, displayName, roomKey, sex }) {
  // Ticket 22: Use roomKey for room-specific channels
  const effectiveRoomKey = roomKey || "flirty";
  const LOUNGE_CHANNEL = `lounge:${effectiveRoomKey}:presence`;
  const SIGNALING_CHANNEL = `lounge:${effectiveRoomKey}:signaling`;
  const normalizedUserSex = normalizeLoungeSex(sex);
  
  debugLog('webrtc', 'useLoungeWebRTC initialized', {
    roomKey: effectiveRoomKey,
    hasUserSex: !!normalizedUserSex,
  });
  const [participants, setParticipants] = useState([]); // {clientId, displayName, joinedAt, sex}
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [currentPartnerId, setCurrentPartnerId] = useState(null);
  const [isPolite, setIsPolite] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'waiting' | 'connecting' | 'connected' | 'error'

  // Ticket 13: WebRTC connection health state
  const [connectionHealth, setConnectionHealth] = useState({
    quality: 'idle',        // 'idle' | 'good' | 'degraded' | 'poor' | 'disconnected'
    bitrateKbps: 0,
    packetLossPercent: 0,
    rttMs: 0,
    lastUpdated: null,
    reason: null,
  });

  const presenceChannelRef = useRef(null);
  const signalChannelRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const hasStartedRef = useRef(false);

  // track the last partner so we can detect partner changes
  const lastPartnerIdRef = useRef(null);
  const currentPartnerIdRef = useRef(null);
  const lastSkippedRef = useRef(null);

  // Ticket 13: Health monitoring refs
  const healthIntervalRef = useRef(null);
  const lastStatsRef = useRef({
    bytesSent: 0,
    timestamp: 0,
  });

  // ---------- helpers ----------

  function log(...args) {
    // prefix all logs for debugging
    // eslint-disable-next-line no-console
    console.log('[lounge-webrtc]', ...args);
  }

  async function ensureLocalStream() {
    if (localStreamRef.current) return localStreamRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      localStreamRef.current = stream;
      setLocalStream(stream);
      log('✅ Acquired local media stream');
      return stream;
    } catch (err) {
      console.error('[lounge-webrtc] ❌ Failed to getUserMedia', err);
      setStatus('error');
      throw err;
    }
  }

  function cleanupPeerConnection() {
    if (pcRef.current) {
      log('🔻 Closing existing RTCPeerConnection');
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.oniceconnectionstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    remoteStreamRef.current = null;
    setRemoteStream(null);
    // 🔥 IMPORTANT: reset handshake state so a new one can start
    hasStartedRef.current = false;
    
    // Ticket 13: Stop health monitor when cleaning up
    stopHealthMonitor();
  }

  // Ticket 13: Health monitoring functions
  const stopHealthMonitor = useCallback(() => {
    if (healthIntervalRef.current) {
      clearInterval(healthIntervalRef.current);
      healthIntervalRef.current = null;
      debugLog('webrtc', 'Stopped health monitor');
    }
  }, []);

  const startHealthMonitor = useCallback((pc) => {
    // Only one monitor at a time
    stopHealthMonitor();

    if (!pc) return;

    debugLog('webrtc', 'Starting health monitor');

    healthIntervalRef.current = window.setInterval(async () => {
      try {
        const stats = await pc.getStats();
        let outboundVideo = null;
        let inboundVideo = null;
        let candidatePair = null;

        stats.forEach((report) => {
          if (report.type === 'outbound-rtp' && report.kind === 'video') {
            outboundVideo = report;
          }
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            inboundVideo = report;
          }
          if (
            report.type === 'candidate-pair' &&
            report.state === 'succeeded' &&
            report.nominated
          ) {
            candidatePair = report;
          }
        });

        const nowTimestamp =
          outboundVideo?.timestamp ||
          candidatePair?.timestamp ||
          Date.now();

        let bitrateKbps = 0;
        let packetLossPercent = 0;
        let rttMs = null;

        // Bitrate estimate from bytesSent delta
        if (outboundVideo && typeof outboundVideo.bytesSent === 'number') {
          const prev = lastStatsRef.current;
          const deltaBytes = outboundVideo.bytesSent - (prev.bytesSent || 0);
          const deltaMs = nowTimestamp - (prev.timestamp || 0);

          if (deltaBytes > 0 && deltaMs > 0) {
            const bits = deltaBytes * 8;
            bitrateKbps = Math.round((bits / deltaMs) * 1000 / 1000);
          }

          lastStatsRef.current = {
            bytesSent: outboundVideo.bytesSent,
            timestamp: nowTimestamp,
          };
        }

        // Packet loss
        if (
          inboundVideo &&
          typeof inboundVideo.packetsLost === 'number' &&
          typeof inboundVideo.packetsReceived === 'number'
        ) {
          const lost = inboundVideo.packetsLost;
          const received = inboundVideo.packetsReceived;
          const total = lost + received;
          packetLossPercent = total > 0 ? (lost / total) * 100 : 0;
        }

        // RTT (round trip) from candidate pair if available
        if (
          candidatePair &&
          typeof candidatePair.currentRoundTripTime === 'number'
        ) {
          rttMs = Math.round(candidatePair.currentRoundTripTime * 1000);
        }

        // Derive quality
        let quality = 'good';
        let reason = [];

        if (rttMs != null) {
          if (rttMs > 400) {
            quality = 'poor';
            reason.push(`high RTT ${rttMs}ms`);
          } else if (rttMs > 200) {
            quality = quality === 'good' ? 'degraded' : quality;
            reason.push(`RTT ${rttMs}ms`);
          }
        }

        if (packetLossPercent > 5) {
          quality = 'poor';
          reason.push(`packet loss ${packetLossPercent.toFixed(1)}%`);
        } else if (packetLossPercent > 2) {
          quality = quality === 'good' ? 'degraded' : quality;
          reason.push(`packet loss ${packetLossPercent.toFixed(1)}%`);
        }

        if (!currentPartnerId) {
          quality = 'idle';
          reason.push('no partner');
        }

        const health = {
          quality,
          bitrateKbps,
          packetLossPercent: Number(packetLossPercent.toFixed(1)),
          rttMs: rttMs ?? 0,
          lastUpdated: Date.now(),
          reason: reason.join(', ') || null,
        };

        setConnectionHealth(health);
        debugLog('webrtc', 'Health sample', health);
      } catch (err) {
        debugLog('webrtc', 'Health monitor error', { error: String(err) });
      }
    }, 3000); // every 3s
  }, [stopHealthMonitor, currentPartnerId]);

  // Ticket 13: Cleanup health monitor on unmount
  useEffect(() => {
    return () => {
      stopHealthMonitor();
    };
  }, [stopHealthMonitor]);

  function broadcastHangup(signalChannel, partnerId) {
    if (!signalChannel || !partnerId) return;
    log('📴 Sending HANGUP to', partnerId);
    signalChannel.publish('signal', {
      type: 'hangup',
      from: userId,
    });
  }

  function getPairKey(a, b) {
    return [a, b].sort().join('_');
  }

  function createPeerConnection(signalChannel, partnerId) {
    log('🌐 Creating RTCPeerConnection to', partnerId, 'with ICE_SERVERS');
    debugLog('webrtc', 'Created RTCPeerConnection', { partnerId });

    const pc = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
      iceTransportPolicy: 'all',
    });

    const local = localStreamRef.current;
    if (local) {
      local.getTracks().forEach((track) => {
        log('➕ Adding local track', track.kind, 'to peer connection');
        pc.addTrack(track, local);
      });
    }

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        log('🧊 Sending ICE candidate to', partnerId, event.candidate.type);
        debugLog('webrtc', 'Sending ICE candidate', { 
          hasCandidate: !!event.candidate,
          type: event.candidate.type 
        });
        signalChannel.publish('signal', {
          type: 'ice-candidate',
          from: userId,
          candidate: event.candidate.toJSON(),
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      log('❄️ iceConnectionState:', st);
      debugLog('webrtc', 'ICE connection state', { state: st });
      
      if (st === 'connected') {
        setStatus('connected');
        startHealthMonitor(pc);
      } else if (st === 'failed' || st === 'disconnected') {
        log('⚠️ ICE connection issue, state:', st);
        stopHealthMonitor();
        setConnectionHealth((prev) => ({
          ...prev,
          quality: 'disconnected',
          reason: `ICE state ${st}`,
        }));
      }
      
      if (st === 'closed') {
        stopHealthMonitor();
      }
    };

    pc.onconnectionstatechange = () => {
      log('🔗 connectionState:', pc.connectionState);
      debugLog('webrtc', 'Peer connection state', { state: pc.connectionState });
    };

    pc.onsignalingstatechange = () => {
      debugLog('webrtc', 'Signaling state', { state: pc.signalingState });
    };

    pc.ontrack = (event) => {
      const [stream] = event.streams || [];
      if (!stream) {
        log('⚠️ ontrack fired but no stream');
        return;
      }
      log(
        '📡 ontrack fired. Received remote stream with',
        stream.getTracks().length,
        'tracks'
      );
      debugLog('webrtc', 'ontrack fired', {
        kinds: stream.getTracks().map((t) => t.kind),
      });
      remoteStreamRef.current = stream;
      setRemoteStream(stream);
    };

    pcRef.current = pc;
    return pc;
  }

  // ---------- signaling handlers ----------

  async function handleRemoteOffer(signalChannel, fromUserId, sdp) {
    log('📥 Received OFFER from', fromUserId);
    debugLog('webrtc', 'Received OFFER', { from: fromUserId });
    
    await ensureLocalStream();
    const pc = createPeerConnection(signalChannel, fromUserId);

    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    log('📤 Sending ANSWER to', fromUserId);
    debugLog('webrtc', 'Sending ANSWER', { to: fromUserId });
    
    signalChannel.publish('signal', {
      type: 'answer',
      from: userId,
      sdp: answer,
    });
  }

  async function handleRemoteAnswer(sdp) {
    const pc = pcRef.current;
    if (!pc) {
      log('⚠️ Received ANSWER but no RTCPeerConnection exists');
      return;
    }
    log('📥 Received ANSWER, setting remote description');
    debugLog('webrtc', 'Received ANSWER');
    
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
  }

  async function handleRemoteCandidate(candidate) {
    const pc = pcRef.current;
    if (!pc) {
      log('⚠️ Received ICE candidate but no RTCPeerConnection exists');
      return;
    }
    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
      log('🧊 Added remote ICE candidate');
      debugLog('webrtc', 'Received ICE candidate', {
        sdpMid: candidate?.sdpMid,
        sdpMLineIndex: candidate?.sdpMLineIndex,
      });
    } catch (err) {
      console.error('[lounge-webrtc] ❌ Failed to add ICE candidate', err);
    }
  }

  async function startAsPolite(signalChannel, partnerId) {
    if (hasStartedRef.current) {
      log('⏭️ startAsPolite called but already started, skipping');
      return;
    }
    hasStartedRef.current = true;
    setStatus('connecting');

    await ensureLocalStream();
    const pc = createPeerConnection(signalChannel, partnerId);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    log('📤 Sending OFFER to', partnerId);
    debugLog('webrtc', 'Sending OFFER', { to: partnerId });
    
    signalChannel.publish('signal', {
      type: 'offer',
      from: userId,
      sdp: offer,
    });
  }

  // ---------- presence + pairing ----------

  // 🔥 Reset state when room changes to prevent stuck pairing
  useEffect(() => {
    log('🔄 Room changed to:', effectiveRoomKey, '→ Resetting state');
    setCurrentPartnerId(null);
    lastPartnerIdRef.current = null;
    lastSkippedRef.current = null;
    setStatus('idle');
    cleanupPeerConnection();
  }, [effectiveRoomKey]);

  useEffect(() => {
    currentPartnerIdRef.current = currentPartnerId;
  }, [currentPartnerId]);

  useEffect(() => {
    if (!ably || !userId) return;

    const presenceChannel = ably.channels.get(LOUNGE_CHANNEL);
    let syncInterval = null;
    let isActive = true;

    presenceChannelRef.current = presenceChannel;
    
    // Add safe error handlers to prevent crashes on channel lifecycle events
    const safeChannelErrorHandler = (err) => {
      console.error('[Lounge WebRTC] Presence channel error:', err);
      // DO NOT throw - just log
    };
    const safeChannelDetachedHandler = (stateChange) => {
      console.warn('[Lounge WebRTC] Presence channel detached:', stateChange?.reason || '');
      // DO NOT throw - normal lifecycle event when switching rooms
    };
    const safeChannelFailedHandler = (stateChange) => {
      console.error('[Lounge WebRTC] Presence channel failed:', stateChange?.reason || '');
      // DO NOT throw
    };
    
    presenceChannel.off('error', safeChannelErrorHandler);
    presenceChannel.off('detached', safeChannelDetachedHandler);
    presenceChannel.off('failed', safeChannelFailedHandler);
    presenceChannel.on('error', safeChannelErrorHandler);
    presenceChannel.on('detached', safeChannelDetachedHandler);
    presenceChannel.on('failed', safeChannelFailedHandler);

    let unsubPresence = null;
    let signalUnsubscribe = null;

    async function enterPresence() {
      try {
        await presenceChannel.presence.enter(
          buildLoungePresenceData({
            displayName,
            sex: normalizedUserSex,
          })
        );
        log('Entered lounge presence as', userId);
      } catch (err) {
        console.error('[lounge-webrtc] Failed to enter presence', err);
      }
    }

    function getBlockedUsers() {
      try {
        const blockedUsersJson = localStorage.getItem('lounge_blocked_users');
        return blockedUsersJson ? JSON.parse(blockedUsersJson) : [];
      } catch (err) {
        console.error('[lounge-webrtc] Failed to parse blocked users list', err);
        return [];
      }
    }

    function recomputeParticipantsAndPairing() {
      if (!isActive) return;

      presenceChannel.presence.get((err, members) => {
        if (!isActive) return;

        if (err) {
          console.error('[lounge-webrtc] presence.get error', err);
          return;
        }

        log('Raw presence members:', members.length, members.map((m) => m.clientId));

        const mapped = members
          .map((m) => ({
            clientId: m.clientId,
            displayName: m.data?.displayName || m.clientId,
            sex: normalizeLoungeSex(m.data?.sex),
            joinedAt: m.data?.joinedAt || 0,
          }))
          .sort(
            (a, b) =>
              a.joinedAt - b.joinedAt ||
              a.clientId.localeCompare(b.clientId)
          );

        // === DEBUG: Pairing diagnostics ===
        console.log('[lounge-pairing-debug] roomKey:', effectiveRoomKey);
        console.log('[lounge-pairing-debug] userId:', userId);
        console.log('[lounge-pairing-debug] normalizedUserSex:', normalizedUserSex);
        console.log('[lounge-pairing-debug] mapped participants:', mapped.map(p => ({
          clientId: p.clientId,
          sex: p.sex,
          joinedAt: p.joinedAt,
        })));
        // Defensive: warn about missing sex values
        mapped.forEach(p => {
          if (!p.sex || p.sex === 'unknown') {
            console.warn(`[lounge-pairing-debug] participant ${p.clientId} has sex="${p.sex}" — may be ineligible for pairing`);
          }
        });

        setParticipants(mapped);
        log(
          'Participants updated:',
          mapped.length,
          mapped.map((p) => p.clientId)
        );

        // pairing
        const ids = mapped.map((m) => m.clientId);
        const myIndex = ids.indexOf(userId);
        const lastSkipped = lastSkippedRef.current;

        if (myIndex === -1) {
          log('Not found in participants list');
          setCurrentPartnerId(null);
          setStatus('waiting');
          return;
        }

        if (!normalizedUserSex) {
          log('Missing or invalid local user sex; cannot compute lounge pairing');
          setCurrentPartnerId(null);
          currentPartnerIdRef.current = null;
          lastPartnerIdRef.current = null;
          setStatus('waiting');
          cleanupPeerConnection();
          return;
        }

        const blockedUsers = getBlockedUsers();

        const partner = selectEligibleLoungePartner({
          mappedParticipants: mapped,
          userId,
          currentUserSex: normalizedUserSex,
          blockedUsers,
          lastSkippedId: lastSkipped,
          currentPartnerId: currentPartnerIdRef.current,
        });

        // === DEBUG: Partner selection result ===
        console.log('[lounge-pairing-debug] selectedPartner:', partner || 'NONE');
        if (!partner) {
          console.log('[lounge-pairing-debug] reason: no eligible partner found among', mapped.length, 'participants');
          console.log('[lounge-pairing-debug] blockedUsers:', blockedUsers);
          console.log('[lounge-pairing-debug] lastSkippedId:', lastSkipped);
        }

        // If no partner, clean up any existing connection and reset
        if (!partner) {
          log('Waiting for a match (no partner yet)');
          setCurrentPartnerId(null);
          lastPartnerIdRef.current = null;
          setStatus('waiting');
          currentPartnerIdRef.current = null;
          cleanupPeerConnection();
          if (signalUnsubscribe) {
            signalUnsubscribe();
            signalUnsubscribe = null;
          }
          return;
        }

        // Detect partner change and clean/reset if needed
        if (
          lastPartnerIdRef.current &&
          lastPartnerIdRef.current !== partner
        ) {
          log(
            'Partner changed from',
            lastPartnerIdRef.current,
            'to',
            partner,
            '→ Auto-requeue: cleaning up old connection'
          );
          cleanupPeerConnection();
        }

        lastPartnerIdRef.current = partner;

        log('Paired with partner', partner);
        debugLog('webrtc', 'Paired with partner', { partnerId: partner });
        setCurrentPartnerId(partner);
        currentPartnerIdRef.current = partner;
        
        // Set status to connecting when we get a new partner
        if (partner && partner !== currentPartnerId) {
          log('✨ New partner assigned, ready to connect');
        }

        // Clear lastSkipped once we successfully pair with someone else
        if (lastSkippedRef.current && partner !== lastSkippedRef.current) {
          lastSkippedRef.current = null;
        }

        const polite = userId < partner; // deterministic polite/impolite
        setIsPolite(polite);

        const pairKey = getPairKey(userId, partner);
        const signalChannel = ably.channels.get(
          `lounge:${effectiveRoomKey}:pair:${pairKey}`
        );
        signalChannelRef.current = signalChannel;
        
        // Add safe error handlers to signal channel
        const safeSignalErrorHandler = (err) => {
          console.error('[Lounge WebRTC] Signal channel error:', err);
          // DO NOT throw
        };
        const safeSignalDetachedHandler = (stateChange) => {
          console.warn('[Lounge WebRTC] Signal channel detached:', stateChange?.reason || '');
          // DO NOT throw - normal when switching partners/rooms
        };
        
        signalChannel.off('error', safeSignalErrorHandler);
        signalChannel.off('detached', safeSignalDetachedHandler);
        signalChannel.on('error', safeSignalErrorHandler);
        signalChannel.on('detached', safeSignalDetachedHandler);

        // subscribe to signaling
        if (signalUnsubscribe) {
          signalUnsubscribe();
          signalUnsubscribe = null;
        }

        const handler = async (msg) => {
          const data = msg.data || {};
          if (!data || data.from === userId) return;

          log(
            '📨 Received signal message',
            data.type,
            'from',
            data.from
          );

          if (data.type === 'offer') {
            // impolite peer receives offer
            hasStartedRef.current = true;
            setStatus('connecting');
            await handleRemoteOffer(signalChannel, data.from, data.sdp);
          } else if (data.type === 'answer') {
            await handleRemoteAnswer(data.sdp);
          } else if (data.type === 'ice-candidate') {
            await handleRemoteCandidate(data.candidate);
          } else if (data.type === 'hangup') {
            log('📴 Received HANGUP from partner', data.from);
            cleanupPeerConnection();
            // Go back to waiting; pairing logic will decide what's next
            setStatus('waiting');
          }
        };

        signalChannel.subscribe('signal', handler);
        signalUnsubscribe = () =>
          signalChannel.unsubscribe('signal', handler);

        // 🔥 Handshake trigger logic:
        // If we're the polite peer and camera is already ON,
        // start offer immediately (but only once).
        if (polite) {
          log(
            '🟢 Acting as polite peer. Will start offer when camera is on.'
          );
          if (localStreamRef.current && !hasStartedRef.current) {
            log(
              '🚀 Camera already on and no handshake yet; starting polite offer now.'
            );
            startAsPolite(signalChannel, partner);
          }
        } else {
          log('🟡 Acting as impolite peer. Waiting for offer.');
        }
      });
    }

    // listen to presence events
    const presenceListener = (msg) => {
      log('Presence event:', msg.action, msg.clientId);
      
      // Detect if our current partner just left — use ref to avoid stale closure
      if (msg.action === 'leave' && msg.clientId === currentPartnerIdRef.current) {
        log('Partner left lounge:', msg.clientId, '→ Auto-requeue');
        cleanupPeerConnection();
        setCurrentPartnerId(null);
        currentPartnerIdRef.current = null;
        setStatus('waiting');
      }

      // Always recompute to find new matches
      recomputeParticipantsAndPairing();
    };

    async function init() {
      await enterPresence();
      // initial fetch
      recomputeParticipantsAndPairing();
      // subscribe to presence updates - listen to all actions
      presenceChannel.presence.subscribe(['enter', 'leave', 'update'], presenceListener);
      unsubPresence = () =>
        presenceChannel.presence.unsubscribe(presenceListener);

      // Fallback: periodic sync every 3 seconds to catch any missed presence updates
      syncInterval = window.setInterval(() => {
        log('Periodic presence sync');
        recomputeParticipantsAndPairing();
      }, 3000);
    }

    init();

    return () => {
      log('Cleaning up useLoungeWebRTC');
      isActive = false;

      if (syncInterval) {
        window.clearInterval(syncInterval);
        syncInterval = null;
      }

      if (unsubPresence) {
        unsubPresence();
        unsubPresence = null;
      }
      if (signalUnsubscribe) {
        signalUnsubscribe();
        signalUnsubscribe = null;
      }

      if (presenceChannelRef.current) {
        presenceChannelRef.current.presence
          .leave()
          .catch((err) => {
            console.error(
              '[lounge-webrtc] error leaving presence',
              err
            );
          });
      }

      // Ticket 22: Detach from Ably channels when room changes
      try {
        if (presenceChannelRef.current) {
          safeDetachAblyChannel(presenceChannelRef.current, 'lounge-presence');
        }
        if (signalChannelRef.current) {
          safeDetachAblyChannel(signalChannelRef.current, 'lounge-signaling');
        }
      } catch (e) {
        debugLog('webrtc', 'Error detaching channels on cleanup', { error: e.message });
      }

      cleanupPeerConnection();

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      setLocalStream(null);
      setRemoteStream(null);
      setParticipants([]);
      setCurrentPartnerId(null);
      lastPartnerIdRef.current = null;
      currentPartnerIdRef.current = null;
      setStatus('idle');
    };
  }, [ably, userId, displayName, effectiveRoomKey, normalizedUserSex]); // Ticket 22: Added roomKey to deps for room switching

  // ---------- public controls ----------

  async function turnCameraOn() {
    log('🎥 turnCameraOn called');
    const stream = await ensureLocalStream();

    if (!currentPartnerId || !signalChannelRef.current) {
      log(
        '🕒 No partner or signal channel yet; local stream ready and waiting.'
      );
      return stream;
    }

    // polite peer starts offer
    if (isPolite) {
      log('🚀 Starting as polite peer with partner', currentPartnerId);
      await startAsPolite(signalChannelRef.current, currentPartnerId);
    } else {
      log(
        '⏳ Impolite peer: waiting for offer from partner',
        currentPartnerId
      );
      // Do nothing; handshake will start when we receive the offer.
    }
    return stream;
  }

  function turnCameraOff() {
    log('🛑 turnCameraOff called');
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
    cleanupPeerConnection();
    setStatus(currentPartnerId ? 'waiting' : 'idle');
  }

  function skipPartner() {
    if (!currentPartnerId) {
      log('⏭️ skipPartner called but no active partner');
      return;
    }

    const partnerId = currentPartnerId;
    log('⏭️ Skipping partner', partnerId, '→ Auto-requeue');
    debugLog('webrtc', 'Skip pressed', { currentPartnerId: partnerId });

    // Remember who we just skipped so we don't instantly re-pair with them
    lastSkippedRef.current = partnerId;

    // Tell the partner to hang up as well
    if (signalChannelRef.current) {
      broadcastHangup(signalChannelRef.current, partnerId);
    }

    // Close our peer connection and go back to waiting
    cleanupPeerConnection();
    setCurrentPartnerId(null);
    currentPartnerIdRef.current = null;
    setStatus('waiting');
  }

  return {
    participants,
    localStream,
    remoteStream,
    status, // 'idle' | 'waiting' | 'connecting' | 'connected' | 'error'
    currentPartnerId,
    isPolite,
    turnCameraOn,
    turnCameraOff,
    skipPartner,
    connectionHealth, // Ticket 13: Return health state
  };
}
